import { useState, useEffect } from 'react';
import {
  ClipboardList,
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Copy,
  ArrowRight,
  Building,
  User,
  Grid3X3,
  List
} from 'lucide-react';
import ModalNuevaCotizacion from './ModalNuevaCotizacion';
import { useEmpresa } from '../contexts/EmpresaContext';
import '../styles/Cotizaciones.css';

const Cotizaciones = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todas');
  const [viewMode, setViewMode] = useState('table');
  const [dateFilter, setDateFilter] = useState('todas');
  const [modalOpen, setModalOpen] = useState(false);
  const [themeColor, setThemeColor] = useState('#c4fffc');
  const { empresaActiva } = useEmpresa();

  // Actualizar color del tema en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  // Usar la moneda de la empresa activa
  const currency = empresaActiva?.monedaDefault || 'CLP';

  const exchangeRates = {
    CLP: 1,
    USD: 950,
    UF: 36500
  };

  const convertCurrency = (amountCLP, toCurrency) => {
    const amount = parseFloat(amountCLP);
    if (toCurrency === 'CLP') {
      return `$${amount.toLocaleString('es-CL')}`;
    } else if (toCurrency === 'USD') {
      const usdAmount = (amount / exchangeRates.USD);
      return `US$${usdAmount.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else if (toCurrency === 'UF') {
      const ufAmount = (amount / exchangeRates.UF);
      return `${ufAmount.toLocaleString('es-CL', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} UF`;
    }
    return `$${amount.toLocaleString('es-CL')}`;
  };

  const cotizaciones = [
    {
      id: 'COT-2025-001',
      cliente: 'Minera Escondida',
      contacto: 'Juan Pérez',
      fecha: '2025-01-14',
      vencimiento: '2025-01-21',
      items: 15,
      monto: 45678900,
      margen: '12%',
      estado: 'Pendiente',
      urgente: true,
      descripcion: 'Válvulas alta presión WEIR 3000PSI'
    },
    {
      id: 'COT-2025-002',
      cliente: 'Codelco División Andina',
      contacto: 'María Silva',
      fecha: '2025-01-13',
      vencimiento: '2025-01-20',
      items: 8,
      monto: 23450000,
      margen: '15%',
      estado: 'Enviada',
      urgente: false,
      descripcion: 'Repuestos Komatsu 930E-4'
    },
    {
      id: 'COT-2025-003',
      cliente: 'Anglo American',
      contacto: 'Carlos Rodríguez',
      fecha: '2025-01-12',
      vencimiento: '2025-01-19',
      items: 23,
      monto: 78900000,
      margen: '10%',
      estado: 'En Revisión',
      urgente: false,
      descripcion: 'EPP certificado para altura'
    },
    {
      id: 'COT-2025-004',
      cliente: 'BHP Billiton',
      contacto: 'Ana Martínez',
      fecha: '2025-01-11',
      vencimiento: '2025-01-18',
      items: 5,
      monto: 123450000,
      margen: '18%',
      estado: 'Aprobada',
      urgente: false,
      descripcion: 'Sistema de filtrado para planta'
    },
    {
      id: 'COT-2025-005',
      cliente: 'Antofagasta Minerals',
      contacto: 'Pedro González',
      fecha: '2025-01-10',
      vencimiento: '2025-01-17',
      items: 12,
      monto: 56780000,
      margen: '14%',
      estado: 'Pendiente',
      urgente: true,
      descripcion: 'Correas transportadoras 72"'
    },
    {
      id: 'COT-2025-006',
      cliente: 'Kinross Chile',
      contacto: 'Luis Fernández',
      fecha: '2025-01-09',
      vencimiento: '2025-01-16',
      items: 18,
      monto: 34567000,
      margen: '11%',
      estado: 'Vencida',
      urgente: false,
      descripcion: 'Bombas centrifugas Goulds'
    }
  ];

  const filteredCotizaciones = cotizaciones.filter(cot => {
    const matchesSearch =
      cot.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cot.cliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cot.descripcion.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === 'todas' ||
      cot.estado.toLowerCase() === filterStatus.toLowerCase();

    // Date filter logic
    const cotDate = new Date(cot.fecha);
    const now = new Date();
    const matchesDate = (() => {
      switch (dateFilter) {
        case 'hoy':
          return cotDate.toDateString() === now.toDateString();
        case 'semana':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return cotDate >= weekAgo;
        case 'mes':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return cotDate >= monthAgo;
        case 'año':
          return cotDate.getFullYear() === now.getFullYear();
        default:
          return true;
      }
    })();

    return matchesSearch && matchesFilter && matchesDate;
  });

  const getStatusColor = (status) => {
    switch(status.toLowerCase()) {
      case 'aprobada': return 'status-aprobada';
      case 'pendiente': return 'status-pendiente';
      case 'enviada': return 'status-enviada';
      case 'en revisión': return 'status-revision';
      case 'vencida': return 'status-vencida';
      default: return '';
    }
  };

  const getStatusStyle = (status) => {
    if (status.toLowerCase() === 'vencida') {
      return {
        color: themeColor,
        borderColor: themeColor,
        border: `2px solid ${themeColor}`,
        padding: '4px 8px',
        borderRadius: '4px',
        fontWeight: '600'
      };
    }
    return {};
  };

  return (
    <div className="cotizaciones-container">
      <div className="cotizaciones-header">
        <div className="header-title">
          <ClipboardList size={28} />
          <h1>Cotizaciones</h1>
        </div>
        <button
          className="btn-new"
          onClick={() => setModalOpen(true)}
          style={{
            backgroundColor: themeColor,
            color: '#000',
            border: '2px solid #000'
          }}
        >
          <Plus size={20} />
          Nueva Cotización
        </button>
      </div>

      <div className="cotizaciones-stats">
        <div className="stat-card">
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
          </div>
          <div className="stat-content">
            <p>Pendientes</p>
            <h3>8</h3>
          </div>
        </div>
        <div className="stat-card">
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px',
            fontSize: '24px'
          }}>
            ⚠
          </div>
          <div className="stat-content">
            <p>Urgentes</p>
            <h3>3</h3>
          </div>
        </div>
        <div className="stat-card">
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <div className="stat-content">
            <p>Por Vencer</p>
            <h3>5</h3>
          </div>
        </div>
        <div className="stat-card">
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: themeColor,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div className="stat-content">
            <p>Valor Total</p>
            <h3>{convertCurrency(437631900, currency)}</h3>
          </div>
        </div>
      </div>

      <div className="cotizaciones-controls">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar por ID, cliente o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="controls-right">
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              style={viewMode === 'table' ? {
                backgroundColor: themeColor,
                color: '#000',
                borderColor: '#000'
              } : {}}
            >
              <List size={16} />
              Tabla
            </button>
            <button
              className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              style={viewMode === 'cards' ? {
                backgroundColor: themeColor,
                color: '#000',
                borderColor: '#000'
              } : {}}
            >
              <Grid3X3 size={16} />
              Cards
            </button>
          </div>
          <select
            className="filter-select"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="todas">Todas las fechas</option>
            <option value="hoy">Hoy</option>
            <option value="semana">Última semana</option>
            <option value="mes">Último mes</option>
            <option value="año">Este año</option>
          </select>
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="todas">Todos los estados</option>
            <option value="pendiente">Pendientes</option>
            <option value="enviada">Enviadas</option>
            <option value="en revisión">En Revisión</option>
            <option value="aprobada">Aprobadas</option>
            <option value="vencida">Vencidas</option>
          </select>
          <div className="currency-display">
            <span style={{fontSize: '16px', fontWeight: 'bold'}}>$</span>
            <span>{currency}</span>
          </div>
          <button className="btn-export">
            <Download size={20} />
            Exportar
          </button>
        </div>
      </div>

{viewMode === 'table' ? (
        <div className="cotizaciones-table-container">
          <table className="cotizaciones-table">
            <thead>
              <tr>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>ID Cotización</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Cliente</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Contacto</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Descripción</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Items</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Valor</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Margen</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Fecha</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Vencimiento</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Estado</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredCotizaciones.map((cot) => (
                <tr key={cot.id} className={cot.urgente ? 'row-urgent' : ''}>
                  <td className="id-cell">
                    {cot.urgente && <span className="urgent-icon" style={{fontSize: '16px', fontWeight: 'bold'}}>⚠</span>}
                    {cot.id}
                  </td>
                  <td>
                    <div className="client-cell">
                      <Building size={16} />
                      {cot.cliente}
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      <User size={14} />
                      {cot.contacto}
                    </div>
                  </td>
                  <td className="description-cell" title={cot.descripcion}>
                    {cot.descripcion.length > 30
                      ? `${cot.descripcion.substring(0, 30)}...`
                      : cot.descripcion}
                  </td>
                  <td className="items-cell">{cot.items}</td>
                  <td className="amount-cell">{convertCurrency(cot.monto, currency)}</td>
                  <td className="margin-cell">{cot.margen}</td>
                  <td>{cot.fecha}</td>
                  <td>{cot.vencimiento}</td>
                  <td>
                    <span
                      className={`status ${getStatusColor(cot.estado)}`}
                      style={getStatusStyle(cot.estado)}
                    >
                      {cot.estado}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-action" title="Ver">
                        <Eye size={16} />
                      </button>
                      <button className="btn-action" title="Editar">
                        <Edit size={16} />
                      </button>
                      <button className="btn-action" title="Duplicar">
                        <Copy size={16} />
                      </button>
                      <button className="btn-action btn-convert" title="Convertir a OV">
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="cotizaciones-cards">
          {filteredCotizaciones.map((cot) => (
            <div key={cot.id} className={`cotizacion-card ${cot.urgente ? 'urgent' : ''}`}>
              {cot.urgente && (
                <div className="urgent-badge">
                  <span style={{fontSize: '14px', fontWeight: 'bold'}}>⚠</span>
                  URGENTE
                </div>
              )}
              <div className="card-header">
                <div className="card-id">
                  <h3>{cot.id}</h3>
                  <span
                    className={`status ${getStatusColor(cot.estado)}`}
                    style={getStatusStyle(cot.estado)}
                  >
                    {cot.estado}
                  </span>
                </div>
                <div className="card-amount">
                  <span className="amount-value">{convertCurrency(cot.monto, currency)}</span>
                  <span className="margin-value">{cot.margen} margen</span>
                </div>
              </div>
              <div className="card-content">
                <div className="client-info">
                  <div className="client-name">
                    <Building size={16} />
                    <span>{cot.cliente}</span>
                  </div>
                  <div className="contact-name">
                    <User size={14} />
                    <span>{cot.contacto}</span>
                  </div>
                </div>
                <div className="description">
                  <p>{cot.descripcion}</p>
                </div>
                <div className="card-details">
                  <div className="detail-item">
                    <strong>Items:</strong> {cot.items}
                  </div>
                  <div className="detail-item">
                    <span style={{fontSize: '14px'}}>⌛</span>
                    <span>Fecha: {cot.fecha}</span>
                  </div>
                  <div className="detail-item">
                    <span style={{fontSize: '14px'}}>⏱</span>
                    <span>Vence: {cot.vencimiento}</span>
                  </div>
                </div>
              </div>
              <div className="card-actions">
                <button className="btn-action" title="Ver">
                  <Eye size={16} />
                </button>
                <button className="btn-action" title="Editar">
                  <Edit size={16} />
                </button>
                <button className="btn-action" title="Duplicar">
                  <Copy size={16} />
                </button>
                <button className="btn-action btn-convert" title="Convertir a OV">
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ModalNuevaCotizacion
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

export default Cotizaciones;